import { useMyCasesLanding } from '@hooks/useMyCasesLanding';
import { useUserTier } from '@hooks/useUserTier';
import { useStore } from '@store/index';
import { buildOrgRoutePath, buildRoutePath } from '@utils/routes';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';

/**
 * Component that redirects to the selected organization's landing page.
 * Tier-2 users go to the org-scoped cases route (/org-{id}/cases); users who can
 * only see the cases assigned to them (Case Managers) go to My Cases
 * (/org-{id}/my-cases); everyone else to their selected program's cases. Shows a
 * loading state until the org (and, for non-tier2, program), tier and landing are
 * resolved.
 */
export const RedirectToSelectedOrg: React.FC = () => {
  const { selectedOrganizationId, selectedProgramId } = useStore();
  const { tier, isLoading: tierLoading } = useUserTier();
  const { isResolving: landingResolving, landOnMyCases } = useMyCasesLanding(
    selectedOrganizationId,
    selectedProgramId,
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (!selectedOrganizationId || tierLoading) {
      return;
    }
    // Tier-2: org-wide cases page, no program needed in the URL.
    if (tier === 'tier2') {
      navigate(buildOrgRoutePath('/cases', selectedOrganizationId));
      return;
    }
    if (!selectedProgramId || landingResolving) {
      return;
    }
    // Assigned-only users start on the cases assigned to them (BV-2347).
    if (landOnMyCases) {
      navigate(buildOrgRoutePath('/my-cases', selectedOrganizationId));
      return;
    }
    // Everyone else: redirect to their selected program's cases page.
    navigate(buildRoutePath('/cases', selectedOrganizationId, selectedProgramId));
  }, [
    selectedOrganizationId,
    selectedProgramId,
    tier,
    tierLoading,
    landingResolving,
    landOnMyCases,
    navigate,
  ]);

  // Show loading state while waiting for organization/program selection
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto"></div>
        <p className="mt-3 text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
};
